import React, { FC, useState, FocusEvent, ChangeEvent, useEffect } from "react";
import { UseFormRegister } from "react-hook-form";
import classNames from "classnames";
import { InlineMessage } from "../InlineMessage/InlineMessage";

export type TextAreaProps = {
	label: string;
	name: string;
	error: any;
	register: UseFormRegister<any>;
	getValues: any;
	maxLength: number;
	rows?: number;
	required?: boolean;
	placeholder?: string;
	disabled?: boolean;
	helpText?: string;
};

export const TextArea: FC<TextAreaProps> = ({
	label,
	name,
	register,
	getValues,
	error,
	maxLength,
	rows = 3,
	required = false,
	placeholder,
	helpText,
}) => {
	const [currentCharactersLength, setCurrentCharactersLength] =
		useState<number>(0);
	const [isFilled, setIsFilled] = useState<boolean>(false);

	const id = `input-${name}`;
	const { onBlur, onChange, ...registerRest } = register(name);

	useEffect(() => {
		if (getValues) {
			setIsFilled(!!getValues(name));
		}
	}, []);

	const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
		setIsFilled(!!e.target.value);
		onBlur(e);
	};

	const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
		setCurrentCharactersLength(e.target.value.length);
		setIsFilled(false);
		onChange(e);
	};

	return (
		<div
			className={classNames(
				"TextArea",
				{ "TextArea--error": !!error },
				{ "TextArea--filled": isFilled && !error }
			)}
		>
			<label className="TextArea__label" htmlFor={id}>
				<span dangerouslySetInnerHTML={{ __html: label }} />
			</label>

			<div className="TextArea__field-wrapper">
				<textarea
					className="TextArea__field"
					{...registerRest}
					onChange={handleChange}
					onBlur={handleBlur}
					id={id}
					name={name}
					rows={rows}
					required={required}
					aria-describedby={`errorId-${id} helpId-${id}`}
					maxLength={maxLength}
					placeholder={placeholder}
				/>

				<span className="TextArea__characterLength">
					{`${currentCharactersLength}/${maxLength}`}
				</span>
			</div>

			{error?.message && (
				<InlineMessage
					variant="error"
					msg={error.message}
					id={`errorId-${id}`}
					className="TextArea__msg"
				/>
			)}

			{helpText && (
				<InlineMessage
					variant="info"
					msg={helpText}
					id={`helpId-${id}`}
					className="TextArea__msg"
				/>
			)}
		</div>
	);
};
